// Phase 5 push-to-talk (api-phase5.md "Push-to-talk"; design/README.md: T (hold)). While T (or the drawer's big
// button) is held the console records the microphone as 16 kHz 16-bit PCM (the pcm16 worklet live talk uses); on
// release it sends the clip as a WAV to POST /api/events/:id/talkdown/live?deviceIds=…, with its length from the
// samples. WAV plays on every speaker path: NX takes its PCM, and the EyeQ AI VMS plays only WAV or μ-law, so the
// MediaRecorder WebM/Ogg this used to send can't play there (2026-10-31). The clip plays after release.
//   - A tap (released within TAP_MS) records nothing: the console opens the talk-down drawer instead.
//   - The microphone stays open for WARM_MS after a clip so the next one starts without losing the first word, then
//     its tracks stop (the browser's mic indicator goes off).
//   - Permission denied, no microphone, or no AudioWorklet: a clear error, nothing is sent.
//   - Clips stop on their own at MAX_MS (a minute of 16 kHz PCM is under 2 MB of the 4 MB limit).

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText, type TalkdownResult } from '../api.ts';
import workletUrl from './pcm-worklet.js?url&no-inline';
import { pcmMs, wavOf } from './wav.ts';

export const TAP_MS = 350;
const MIN_MS = 600;
const MAX_MS = 60_000;
const WARM_MS = 30_000;
const MAX_BYTES = 4 * 1024 * 1024;
const RATE = 16_000;

export type PttPhase = 'idle' | 'opening' | 'recording' | 'sending';

export interface PttState {
  phase: PttPhase;
  /** When recording started (for the timer). */
  startedAt: number | null;
  /** The speakers the clip goes to (names for the indicator). */
  target: { eventId: string; deviceIds: string[]; label: string } | null;
  /** The last problem: permission denied, no microphone, upload failed. */
  error: string | null;
  denied: boolean;
}

export interface Ptt extends PttState {
  /** Supported by this browser (getUserMedia and AudioWorklet). */
  supported: boolean;
  press: (target: { eventId: string; deviceIds: string[]; label: string }) => void;
  /** Returns 'tap' when it was released too soon to be a clip (nothing is sent). */
  release: () => 'tap' | 'sent' | 'none';
  cancel: () => void;
  clearError: () => void;
}

/** One clip being recorded: the worklet's frames until it stops. */
interface Recording {
  ctx: AudioContext;
  frames: ArrayBuffer[];
  bytes: number;
  stopped: boolean;
  target: { eventId: string; deviceIds: string[]; label: string };
}

function micError(e: unknown): { text: string; denied: boolean } {
  const name = e instanceof DOMException ? e.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError')
    return { text: 'Microphone blocked. Allow the microphone for this site in the browser (address bar → site settings), then hold T again.', denied: true };
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return { text: 'No microphone found. Plug in a headset, then hold T again.', denied: false };
  if (name === 'NotReadableError') return { text: 'The microphone is busy in another app. Close it there, then hold T again.', denied: false };
  return { text: `Could not open the microphone: ${errorText(e)}`, denied: false };
}

export function usePushToTalk(onSent: (r: TalkdownResult, target: { eventId: string; label: string; ms: number }) => void, onFail: (msg: string) => void): Ptt {
  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof AudioWorkletNode !== 'undefined';
  const [st, setSt] = useState<PttState>({ phase: 'idle', startedAt: null, target: null, error: null, denied: false });
  const stRef = useRef(st);
  stRef.current = st;
  const stream = useRef<MediaStream | null>(null);
  const rec = useRef<Recording | null>(null);
  const held = useRef(false);
  const pressedAt = useRef(0);
  const recAt = useRef(0);
  const discard = useRef(false);
  const warm = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cbs = useRef({ onSent, onFail });
  cbs.current = { onSent, onFail };

  const stopStream = useCallback(() => {
    if (warm.current) clearTimeout(warm.current);
    warm.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }, []);
  const coolDown = useCallback(() => {
    if (warm.current) clearTimeout(warm.current);
    warm.current = setTimeout(stopStream, WARM_MS);
  }, [stopStream]);
  useEffect(() => () => {
    if (maxT.current) clearTimeout(maxT.current);
    const r = rec.current;
    if (r && !r.stopped) {
      r.stopped = true;
      void r.ctx.close().catch(() => undefined);
    }
    stopStream();
  }, [stopStream]);

  const send = useCallback(
    async (blob: Blob, contentType: string, target: { eventId: string; deviceIds: string[]; label: string }, ms: number) => {
      if (blob.size > MAX_BYTES) {
        setSt((s) => ({ ...s, phase: 'idle', startedAt: null, error: 'The clip is over 4 MB. Keep push-to-talk under a minute.' }));
        cbs.current.onFail('The clip is over 4 MB. Keep push-to-talk under a minute.');
        return;
      }
      setSt((s) => ({ ...s, phase: 'sending' }));
      try {
        const r = await api.talkdownLive(target.eventId, target.deviceIds, blob, contentType, ms);
        setSt((s) => ({ ...s, phase: 'idle', startedAt: null, error: null }));
        cbs.current.onSent(r, { eventId: target.eventId, label: target.label, ms });
      } catch (e) {
        const msg = `Push-to-talk not played: ${errorText(e)}`;
        setSt((s) => ({ ...s, phase: 'idle', startedAt: null, error: msg }));
        cbs.current.onFail(msg);
      }
    },
    [],
  );

  /** Ends the clip: sent as a WAV, or dropped when it was a tap or cancelled. */
  const stopRecording = useCallback(() => {
    const r = rec.current;
    if (!r || r.stopped) return;
    r.stopped = true;
    rec.current = null;
    if (maxT.current) clearTimeout(maxT.current);
    void r.ctx.close().catch(() => undefined);
    coolDown();
    if (discard.current) {
      setSt((x) => ({ ...x, phase: 'idle', startedAt: null }));
      return;
    }
    void send(wavOf(r.frames, RATE), 'audio/wav', r.target, pcmMs(r.bytes, RATE) || Date.now() - recAt.current);
  }, [coolDown, send]);

  const startRecorder = useCallback(
    (s: MediaStream, target: { eventId: string; deviceIds: string[]; label: string }) => {
      let ctx: AudioContext;
      try {
        ctx = new AudioContext();
      } catch (e) {
        setSt((x) => ({ ...x, phase: 'idle', error: `This browser cannot record audio: ${errorText(e)}` }));
        return;
      }
      const r: Recording = { ctx, frames: [], bytes: 0, stopped: false, target };
      rec.current = r;
      discard.current = false;
      recAt.current = Date.now();
      setSt((x) => ({ ...x, phase: 'recording', startedAt: recAt.current }));
      // A clip never runs past MAX_MS: it is sent as it is.
      maxT.current = setTimeout(() => {
        held.current = false;
        stopRecording();
      }, MAX_MS);
      void (async () => {
        await ctx.audioWorklet.addModule(workletUrl);
        if (r.stopped) return;
        const node = new AudioWorkletNode(ctx, 'pcm16', { processorOptions: { rate: RATE } });
        node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
          if (r.stopped) return;
          r.frames.push(e.data);
          r.bytes += e.data.byteLength;
        };
        ctx.createMediaStreamSource(s).connect(node);
        if (ctx.state === 'suspended') await ctx.resume();
      })().catch((e: unknown) => {
        if (r.stopped) return;
        discard.current = true;
        stopRecording();
        const msg = `This browser cannot record audio: ${errorText(e)}`;
        setSt((x) => ({ ...x, error: msg }));
        cbs.current.onFail(msg);
      });
    },
    [stopRecording],
  );

  const press = useCallback(
    (target: { eventId: string; deviceIds: string[]; label: string }) => {
      if (stRef.current.phase !== 'idle') return;
      held.current = true;
      pressedAt.current = Date.now();
      if (!target.deviceIds.length) {
        setSt((s) => ({ ...s, error: 'Pick a speaker first (Talk-down drawer).' }));
        cbs.current.onFail('Pick a speaker first (Talk-down drawer).');
        return;
      }
      if (!supported) {
        setSt((s) => ({ ...s, error: 'This browser cannot record audio (no microphone access or AudioWorklet). Use a library or typed message.' }));
        cbs.current.onFail('This browser cannot record audio. Use a library or typed message.');
        return;
      }
      setSt({ phase: 'opening', startedAt: null, target, error: null, denied: false });
      if (warm.current) clearTimeout(warm.current);
      const live = stream.current && stream.current.getAudioTracks().some((t) => t.readyState === 'live') ? stream.current : null;
      const got = live ? Promise.resolve(live) : navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      got.then(
        (s) => {
          stream.current = s;
          if (!held.current) {
            // Released while the mic was opening (or the permission prompt was up).
            setSt((x) => ({ ...x, phase: 'idle' }));
            coolDown();
            return;
          }
          startRecorder(s, target);
        },
        (e: unknown) => {
          const m = micError(e);
          setSt((x) => ({ ...x, phase: 'idle', error: m.text, denied: m.denied }));
          cbs.current.onFail(m.text);
        },
      );
    },
    [supported, startRecorder, coolDown],
  );

  const release = useCallback((): 'tap' | 'sent' | 'none' => {
    const wasHeld = held.current;
    held.current = false;
    const heldMs = Date.now() - pressedAt.current;
    const r = rec.current;
    const phase = stRef.current.phase;
    if (!wasHeld) return 'none';
    if (phase === 'recording' && r && !r.stopped) {
      if (Date.now() - recAt.current < MIN_MS) {
        discard.current = true;
        stopRecording();
        return heldMs < TAP_MS ? 'tap' : 'none';
      }
      stopRecording();
      return 'sent';
    }
    return heldMs < TAP_MS ? 'tap' : 'none';
  }, [stopRecording]);

  const cancel = useCallback(() => {
    held.current = false;
    const r = rec.current;
    if (r && !r.stopped) {
      discard.current = true;
      stopRecording();
    } else setSt((x) => (x.phase === 'opening' ? { ...x, phase: 'idle' } : x));
  }, [stopRecording]);

  const clearError = useCallback(() => setSt((s) => ({ ...s, error: null, denied: false })), []);

  return { ...st, supported, press, release, cancel, clearError };
}
